const MCU_CATEGORIES = new Set([
  'microcontroller-8bit',
  'microcontroller-16bit',
  'microcontroller-32bit',
  'development-board'
]);

const SENSOR_CATEGORIES = new Set([
  'sensor-module'
]);

const PROTOCOL_LABELS = {
  i2c: 'I2C',
  spi: 'SPI',
  uart: 'UART',
  'one-wire': '1-Wire',
  analog: 'Analog',
  pwm: 'PWM',
  gpio: 'GPIO',
  i3c: 'I3C',
  can: 'CAN',
  'can-fd': 'CAN FD',
  i2s: 'I2S',
  pdm: 'PDM',
  usb: 'USB',
  sdio: 'SDIO',
  ethernet: 'Ethernet',
  ble: 'BLE',
  'bluetooth-classic': 'Bluetooth Classic',
  wifi: 'WiFi',
  pio: 'PIO',
  hstx: 'HSTX',
  'rs-232': 'RS-232',
  'rs-485': 'RS-485',
  'rs-422': 'RS-422',
  lin: 'LIN',
  swd: 'SWD',
  jtag: 'JTAG',
  qspi: 'QSPI',
  ospi: 'OSPI',
  modbus: 'Modbus'
};

const PROTOCOL_PRIORITY = [
  'i2c',
  'spi',
  'uart',
  'one-wire',
  'analog',
  'pwm',
  'gpio',
  'i3c',
  'can',
  'can-fd',
  'i2s',
  'pdm',
  'usb',
  'sdio',
  'ethernet',
  'ble',
  'bluetooth-classic',
  'wifi',
  'pio',
  'hstx',
  'rs-232',
  'rs-485',
  'rs-422',
  'lin',
  'swd',
  'jtag',
  'qspi',
  'ospi',
  'modbus'
];

const STATUS_PRIORITY = {
  pass: 4,
  warning: 3,
  unknown: 2,
  fail: 1
};

const EPSILON = 0.001;

const CAPABILITY_MATCHERS = {
  ground: {
    aliases: ['ground'],
    patterns: [/\bgnd\b/i, /\bground\b/i]
  },
  'i2c-sda': {
    patterns: [/\bsda\b/i]
  },
  'i2c-scl': {
    patterns: [/\bscl\b/i]
  },
  'spi-mosi': {
    patterns: [/\bmosi\b/i, /\bsdi\b/i]
  },
  'spi-miso': {
    patterns: [/\bmiso\b/i, /\bsdo\b/i]
  },
  'spi-sck': {
    patterns: [/\bsck\b/i, /\bsclk\b/i, /\bclk\b/i]
  },
  'spi-cs': {
    aliases: ['chip-select'],
    patterns: [/\bcs\b/i, /\bcsb\b/i, /\bss\b/i, /chip\s*select/i]
  },
  'uart-tx': {
    patterns: [/\btx\b/i]
  },
  'uart-rx': {
    patterns: [/\brx\b/i]
  },
  'analog-in': {
    patterns: [/\banalog\s*in\b/i, /\badc\b/i, /\bain\b/i]
  },
  'analog-out': {
    patterns: [/\banalog\s*out\b/i, /\bdac\b/i, /\baout\b/i]
  },
  pwm: {
    patterns: [/\bpwm\b/i]
  },
  'power-output-3v3': {
    patterns: [/\b3\.?3v\b/i, /\b3v3\b/i],
    scope: 'mcu'
  },
  'power-output-5v': {
    patterns: [/\b5v\b/i],
    scope: 'mcu'
  },
  'power-input': {
    patterns: [/\bvcc\b/i, /\bvin\b/i, /\bvdd\b/i, /\b3\.?3v\b/i, /\b3v3\b/i, /\b5v\b/i],
    scope: 'sensor'
  }
};

const RECEIVER_CAPABILITIES_BY_PROTOCOL = {
  i2c: ['i2c-sda', 'i2c-scl'],
  spi: ['spi-miso'],
  uart: ['uart-rx'],
  analog: ['analog-in'],
  gpio: ['gpio'],
  pwm: ['pwm'],
  'one-wire': ['gpio']
};

const hasTextValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';

const toNumber = (value) => {
  if (!hasTextValue(value)) {
    return null;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const nearlyEqual = (left, right) => left !== null && right !== null && Math.abs(left - right) <= EPSILON;

const normalizeStringArray = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item ?? '').trim().toLowerCase())
      .filter(Boolean)
      .filter((item, index, array) => array.indexOf(item) === index);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return [];
    }

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return normalizeStringArray(parsed);
      }
    } catch (error) {
      return normalizeStringArray(trimmed.split(','));
    }
  }

  return [];
};

const formatDecimal = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return String(value).trim();
  }

  return Number.isInteger(numericValue)
    ? String(numericValue)
    : String(Number(numericValue.toFixed(3)));
};

const formatVoltageRange = (minValue, maxValue) => {
  const minText = formatDecimal(minValue);
  const maxText = formatDecimal(maxValue);

  if (minText && maxText) {
    return minText === maxText ? `${minText}V` : `${minText}-${maxText}V`;
  }

  if (minText) {
    return `${minText}V min`;
  }

  if (maxText) {
    return `${maxText}V max`;
  }

  return '';
};

const formatCurrent = (value) => {
  const text = formatDecimal(value);
  return text ? `${text}mA` : '';
};

const getProtocolLabel = (protocol) => PROTOCOL_LABELS[protocol] || String(protocol || '').toUpperCase();

const sortProtocols = (protocols) => [...protocols].sort((left, right) => {
  const leftIndex = PROTOCOL_PRIORITY.indexOf(left);
  const rightIndex = PROTOCOL_PRIORITY.indexOf(right);

  const safeLeftIndex = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
  const safeRightIndex = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;

  if (safeLeftIndex !== safeRightIndex) {
    return safeLeftIndex - safeRightIndex;
  }

  return left.localeCompare(right);
});

const getPinDisplayName = (pin) => {
  const pinName = String(pin?.pin_name || '').trim();
  if (pinName) {
    return pinName;
  }

  const pinNumber = String(pin?.pin_number || '').trim();
  return pinNumber ? `Pin ${pinNumber}` : 'Unnamed pin';
};

const getUniquePinKey = (pin) => (
  pin?.id
  ?? `${String(pin?.pin_number || '').trim()}::${String(pin?.pin_name || '').trim()}::${String(pin?.svg_id || '').trim()}`
);

const formatPinList = (pins) => (
  pins
    .map(getPinDisplayName)
    .filter(Boolean)
    .filter((value, index, array) => array.indexOf(value) === index)
    .join(', ')
);

const getPinSearchText = (pin) => [
  pin?.pin_name,
  pin?.functions,
  pin?.alternate_functions,
  pin?.description
]
  .filter(hasTextValue)
  .join(' ')
  .toLowerCase();

const matchesCapability = (pin, capability, boardKind) => {
  const normalizedCapability = String(capability || '').trim().toLowerCase();
  const pinRole = String(pin?.pin_role || '').trim().toLowerCase();
  const capabilityTags = normalizeStringArray(pin?.capability_tags);
  const matcher = CAPABILITY_MATCHERS[normalizedCapability];
  const aliases = [normalizedCapability, ...(matcher?.aliases || [])];

  if (aliases.includes(pinRole)) {
    return true;
  }

  if (capabilityTags.some((tag) => aliases.includes(tag))) {
    return true;
  }

  if (!matcher || (matcher.scope && matcher.scope !== boardKind)) {
    return false;
  }

  const searchText = getPinSearchText(pin);
  return (matcher.patterns || []).some((pattern) => pattern.test(searchText));
};

const findMatchingPins = (pins, capability, boardKind) => (
  (Array.isArray(pins) ? pins : []).filter((pin, index, array) => (
    matchesCapability(pin, capability, boardKind)
    && array.findIndex((candidate) => getUniquePinKey(candidate) === getUniquePinKey(pin)) === index
  ))
);

const rangeContainsVoltage = (minValue, maxValue, voltage) => {
  const minNumeric = toNumber(minValue);
  const maxNumeric = toNumber(maxValue);

  if (minNumeric === null && maxNumeric === null) {
    return null;
  }

  if (minNumeric !== null && voltage + EPSILON < minNumeric) {
    return false;
  }

  if (maxNumeric !== null && voltage - EPSILON > maxNumeric) {
    return false;
  }

  return true;
};

const evaluateProtocolOverlap = (mcuBoard, sensorBoard) => {
  const mcuProtocols = sortProtocols(normalizeStringArray(mcuBoard?.supported_protocols));
  const sensorProtocols = sortProtocols(normalizeStringArray(sensorBoard?.supported_protocols));
  const sharedProtocols = mcuProtocols.filter((protocol) => sensorProtocols.includes(protocol));

  if (mcuProtocols.length === 0 && sensorProtocols.length === 0) {
    return {
      key: 'protocol-overlap',
      label: 'Protocol Overlap',
      status: 'unknown',
      message: 'Neither board has supported protocol metadata yet.',
      details: [],
      sharedProtocols
    };
  }

  if (mcuProtocols.length === 0 || sensorProtocols.length === 0) {
    return {
      key: 'protocol-overlap',
      label: 'Protocol Overlap',
      status: 'unknown',
      message: 'One of the boards is still missing supported protocol metadata.',
      details: [
        `${mcuBoard?.name || 'MCU'}: ${mcuProtocols.length > 0 ? mcuProtocols.map(getProtocolLabel).join(', ') : 'No protocol data'}`,
        `${sensorBoard?.name || 'Sensor'}: ${sensorProtocols.length > 0 ? sensorProtocols.map(getProtocolLabel).join(', ') : 'No protocol data'}`
      ],
      sharedProtocols
    };
  }

  if (sharedProtocols.length === 0) {
    return {
      key: 'protocol-overlap',
      label: 'Protocol Overlap',
      status: 'fail',
      message: 'The selected boards do not list any shared communication protocol.',
      details: [
        `${mcuBoard?.name || 'MCU'}: ${mcuProtocols.map(getProtocolLabel).join(', ')}`,
        `${sensorBoard?.name || 'Sensor'}: ${sensorProtocols.map(getProtocolLabel).join(', ')}`
      ],
      sharedProtocols
    };
  }

  return {
    key: 'protocol-overlap',
    label: 'Protocol Overlap',
    status: 'pass',
    message: `Both boards list ${sharedProtocols.map(getProtocolLabel).join(', ')} support.`,
    details: [],
    sharedProtocols
  };
};

const evaluateI2CProtocol = (mcuPins, sensorPins) => {
  const mcuSda = findMatchingPins(mcuPins, 'i2c-sda', 'mcu');
  const mcuScl = findMatchingPins(mcuPins, 'i2c-scl', 'mcu');
  const sensorSda = findMatchingPins(sensorPins, 'i2c-sda', 'sensor');
  const sensorScl = findMatchingPins(sensorPins, 'i2c-scl', 'sensor');

  if (mcuSda.length && mcuScl.length && sensorSda.length && sensorScl.length) {
    return {
      protocol: 'i2c',
      status: 'pass',
      message: 'I2C pins are explicitly tagged on both boards.',
      details: [
        `MCU SDA: ${formatPinList(mcuSda)}`,
        `MCU SCL: ${formatPinList(mcuScl)}`,
        `Sensor SDA: ${formatPinList(sensorSda)}`,
        `Sensor SCL: ${formatPinList(sensorScl)}`
      ],
      receiverPins: [...mcuSda, ...mcuScl]
    };
  }

  return {
    protocol: 'i2c',
    status: 'warning',
    message: 'I2C is shared, but the required SDA/SCL pins are not fully tagged yet.',
    details: [
      mcuSda.length ? `MCU SDA: ${formatPinList(mcuSda)}` : 'MCU SDA: not tagged',
      mcuScl.length ? `MCU SCL: ${formatPinList(mcuScl)}` : 'MCU SCL: not tagged',
      sensorSda.length ? `Sensor SDA: ${formatPinList(sensorSda)}` : 'Sensor SDA: not tagged',
      sensorScl.length ? `Sensor SCL: ${formatPinList(sensorScl)}` : 'Sensor SCL: not tagged'
    ],
    receiverPins: [...mcuSda, ...mcuScl]
  };
};

const evaluateSpiProtocol = (mcuPins, sensorPins) => {
  const mcuMosi = findMatchingPins(mcuPins, 'spi-mosi', 'mcu');
  const mcuMiso = findMatchingPins(mcuPins, 'spi-miso', 'mcu');
  const mcuSck = findMatchingPins(mcuPins, 'spi-sck', 'mcu');
  const mcuCs = [
    ...findMatchingPins(mcuPins, 'spi-cs', 'mcu'),
    ...findMatchingPins(mcuPins, 'gpio', 'mcu')
  ];

  const sensorMosi = findMatchingPins(sensorPins, 'spi-mosi', 'sensor');
  const sensorMiso = findMatchingPins(sensorPins, 'spi-miso', 'sensor');
  const sensorSck = findMatchingPins(sensorPins, 'spi-sck', 'sensor');
  const sensorCs = findMatchingPins(sensorPins, 'spi-cs', 'sensor');

  if (mcuMosi.length && mcuMiso.length && mcuSck.length && mcuCs.length && sensorMosi.length && sensorMiso.length && sensorSck.length && sensorCs.length) {
    return {
      protocol: 'spi',
      status: 'pass',
      message: 'SPI pins are explicitly tagged on both boards.',
      details: [
        `MCU MOSI: ${formatPinList(mcuMosi)}`,
        `MCU MISO: ${formatPinList(mcuMiso)}`,
        `MCU SCK: ${formatPinList(mcuSck)}`,
        `MCU CS/GPIO: ${formatPinList(mcuCs)}`,
        `Sensor MOSI: ${formatPinList(sensorMosi)}`,
        `Sensor MISO: ${formatPinList(sensorMiso)}`,
        `Sensor SCK: ${formatPinList(sensorSck)}`,
        `Sensor CS: ${formatPinList(sensorCs)}`
      ],
      receiverPins: [...mcuMiso]
    };
  }

  return {
    protocol: 'spi',
    status: 'warning',
    message: 'SPI is shared, but MOSI/MISO/SCK/CS pins are not fully tagged yet.',
    details: [
      mcuMosi.length ? `MCU MOSI: ${formatPinList(mcuMosi)}` : 'MCU MOSI: not tagged',
      mcuMiso.length ? `MCU MISO: ${formatPinList(mcuMiso)}` : 'MCU MISO: not tagged',
      mcuSck.length ? `MCU SCK: ${formatPinList(mcuSck)}` : 'MCU SCK: not tagged',
      mcuCs.length ? `MCU CS/GPIO: ${formatPinList(mcuCs)}` : 'MCU CS/GPIO: not tagged',
      sensorMosi.length ? `Sensor MOSI: ${formatPinList(sensorMosi)}` : 'Sensor MOSI: not tagged',
      sensorMiso.length ? `Sensor MISO: ${formatPinList(sensorMiso)}` : 'Sensor MISO: not tagged',
      sensorSck.length ? `Sensor SCK: ${formatPinList(sensorSck)}` : 'Sensor SCK: not tagged',
      sensorCs.length ? `Sensor CS: ${formatPinList(sensorCs)}` : 'Sensor CS: not tagged'
    ],
    receiverPins: [...mcuMiso]
  };
};

const evaluateUartProtocol = (mcuPins, sensorPins) => {
  const mcuTx = findMatchingPins(mcuPins, 'uart-tx', 'mcu');
  const mcuRx = findMatchingPins(mcuPins, 'uart-rx', 'mcu');
  const sensorTx = findMatchingPins(sensorPins, 'uart-tx', 'sensor');
  const sensorRx = findMatchingPins(sensorPins, 'uart-rx', 'sensor');

  if (mcuTx.length && mcuRx.length && sensorTx.length && sensorRx.length) {
    return {
      protocol: 'uart',
      status: 'pass',
      message: 'UART TX/RX pins are explicitly tagged on both boards.',
      details: [
        `MCU TX: ${formatPinList(mcuTx)}`,
        `MCU RX: ${formatPinList(mcuRx)}`,
        `Sensor TX: ${formatPinList(sensorTx)}`,
        `Sensor RX: ${formatPinList(sensorRx)}`
      ],
      receiverPins: [...mcuRx]
    };
  }

  return {
    protocol: 'uart',
    status: 'warning',
    message: 'UART is shared, but the TX/RX pins are not fully tagged yet.',
    details: [
      mcuTx.length ? `MCU TX: ${formatPinList(mcuTx)}` : 'MCU TX: not tagged',
      mcuRx.length ? `MCU RX: ${formatPinList(mcuRx)}` : 'MCU RX: not tagged',
      sensorTx.length ? `Sensor TX: ${formatPinList(sensorTx)}` : 'Sensor TX: not tagged',
      sensorRx.length ? `Sensor RX: ${formatPinList(sensorRx)}` : 'Sensor RX: not tagged'
    ],
    receiverPins: [...mcuRx]
  };
};

const evaluateAnalogProtocol = (mcuPins, sensorPins) => {
  const mcuAnalogIn = findMatchingPins(mcuPins, 'analog-in', 'mcu');
  const mcuAnalogOut = findMatchingPins(mcuPins, 'analog-out', 'mcu');
  const sensorAnalogOut = findMatchingPins(sensorPins, 'analog-out', 'sensor');
  const sensorAnalogIn = findMatchingPins(sensorPins, 'analog-in', 'sensor');

  if (mcuAnalogIn.length && sensorAnalogOut.length) {
    return {
      protocol: 'analog',
      status: 'pass',
      message: 'The sensor exposes an analog output and the MCU has an analog input.',
      details: [
        `MCU analog input: ${formatPinList(mcuAnalogIn)}`,
        `Sensor analog output: ${formatPinList(sensorAnalogOut)}`
      ],
      receiverPins: [...mcuAnalogIn]
    };
  }

  if (mcuAnalogOut.length && sensorAnalogIn.length) {
    return {
      protocol: 'analog',
      status: 'pass',
      message: 'The MCU exposes an analog output and the sensor has an analog input.',
      details: [
        `MCU analog output: ${formatPinList(mcuAnalogOut)}`,
        `Sensor analog input: ${formatPinList(sensorAnalogIn)}`
      ],
      receiverPins: []
    };
  }

  return {
    protocol: 'analog',
    status: 'warning',
    message: 'Analog is shared, but the required analog input/output pins are not fully tagged yet.',
    details: [],
    receiverPins: [...mcuAnalogIn]
  };
};

const evaluateSimpleProtocol = (protocol, mcuPins, sensorPins, capability, message) => {
  const mcuMatches = findMatchingPins(mcuPins, capability, 'mcu');
  const sensorMatches = findMatchingPins(sensorPins, capability, 'sensor');

  if (mcuMatches.length && sensorMatches.length) {
    return {
      protocol,
      status: 'pass',
      message,
      details: [
        `MCU: ${formatPinList(mcuMatches)}`,
        `Sensor: ${formatPinList(sensorMatches)}`
      ],
      receiverPins: [...mcuMatches]
    };
  }

  return {
    protocol,
    status: 'warning',
    message: `${getProtocolLabel(protocol)} is shared, but the required pins are not fully tagged yet.`,
    details: [],
    receiverPins: [...mcuMatches]
  };
};

const evaluateProtocolPins = (sharedProtocols, mcuPins, sensorPins) => {
  if (sharedProtocols.length === 0) {
    return {
      key: 'protocol-pins',
      label: 'Protocol Pins',
      status: 'unknown',
      message: 'Add shared protocols first before validating the required pins.',
      details: [],
      recommendedProtocol: null,
      recommendedDetails: [],
      receiverPins: []
    };
  }

  const protocolEvaluations = sharedProtocols.map((protocol) => {
    switch (protocol) {
      case 'i2c':
        return evaluateI2CProtocol(mcuPins, sensorPins);
      case 'spi':
        return evaluateSpiProtocol(mcuPins, sensorPins);
      case 'uart':
        return evaluateUartProtocol(mcuPins, sensorPins);
      case 'analog':
        return evaluateAnalogProtocol(mcuPins, sensorPins);
      case 'gpio':
        return evaluateSimpleProtocol('gpio', mcuPins, sensorPins, 'gpio', 'Both boards have GPIO pins tagged for a simple digital link.');
      case 'pwm':
        return evaluateSimpleProtocol('pwm', mcuPins, sensorPins, 'pwm', 'Both boards have PWM pins tagged.');
      case 'one-wire':
        return evaluateSimpleProtocol('one-wire', mcuPins, sensorPins, 'gpio', 'Both boards have a tagged GPIO pin for a 1-Wire style link.');
      case 'can':
      case 'i2s':
      case 'usb':
        return {
          protocol,
          status: 'warning',
          message: `${getProtocolLabel(protocol)} is shared, but v1 smart compare does not validate those pin mappings yet.`,
          details: [],
          receiverPins: []
        };
      default:
        return {
          protocol,
          status: 'unknown',
          message: `${getProtocolLabel(protocol)} is shared, but there is no validation template for it yet.`,
          details: [],
          receiverPins: []
        };
    }
  });

  const rankedProtocols = [...protocolEvaluations].sort((left, right) => {
    const statusDelta = STATUS_PRIORITY[right.status] - STATUS_PRIORITY[left.status];
    if (statusDelta !== 0) {
      return statusDelta;
    }

    return PROTOCOL_PRIORITY.indexOf(left.protocol) - PROTOCOL_PRIORITY.indexOf(right.protocol);
  });

  const bestProtocol = rankedProtocols[0];
  const hasPass = protocolEvaluations.some((evaluation) => evaluation.status === 'pass');
  const hasWarning = protocolEvaluations.some((evaluation) => evaluation.status === 'warning');
  const overallStatus = hasPass ? 'pass' : hasWarning ? 'warning' : bestProtocol.status;

  return {
    key: 'protocol-pins',
    label: 'Protocol Pins',
    status: overallStatus,
    message: bestProtocol.message,
    details: bestProtocol.details,
    recommendedProtocol: bestProtocol.protocol,
    recommendedDetails: bestProtocol.details,
    receiverPins: bestProtocol.receiverPins || []
  };
};

const evaluatePowerSupply = (mcuBoard, sensorBoard, mcuPins) => {
  const sensorSupplyMin = toNumber(sensorBoard?.supply_voltage_min_v);
  const sensorSupplyMax = toNumber(sensorBoard?.supply_voltage_max_v);
  const mcuLogicMax = toNumber(mcuBoard?.logic_voltage_max_v);

  const railOptions = [
    {
      key: '3v3',
      label: '3.3V',
      voltage: 3.3,
      railLimitMa: toNumber(mcuBoard?.rail_3v3_max_ma),
      pins: findMatchingPins(mcuPins, 'power-output-3v3', 'mcu')
    },
    {
      key: '5v',
      label: '5V',
      voltage: 5,
      railLimitMa: toNumber(mcuBoard?.rail_5v_max_ma),
      pins: findMatchingPins(mcuPins, 'power-output-5v', 'mcu')
    }
  ].map((option) => ({
    ...option,
    acceptsSensorVoltage: rangeContainsVoltage(sensorSupplyMin, sensorSupplyMax, option.voltage),
    isAvailable: option.railLimitMa !== null || option.pins.length > 0
  }));

  const viableOptions = railOptions.filter((option) => option.acceptsSensorVoltage === true && option.isAvailable);
  const maybeOptions = railOptions.filter((option) => option.acceptsSensorVoltage !== false && option.isAvailable);
  const preferredOption = viableOptions.find((option) => nearlyEqual(option.voltage, mcuLogicMax))
    || viableOptions.find((option) => nearlyEqual(option.voltage, 3.3))
    || viableOptions[0]
    || maybeOptions.find((option) => nearlyEqual(option.voltage, mcuLogicMax))
    || maybeOptions.find((option) => nearlyEqual(option.voltage, 3.3))
    || maybeOptions[0]
    || null;

  const sensorRangeText = formatVoltageRange(sensorSupplyMin, sensorSupplyMax);

  if (viableOptions.length > 0) {
    const viableLabels = viableOptions.map((option) => option.label);
    const detailLines = [
      sensorRangeText ? `Sensor supply range: ${sensorRangeText}` : null,
      ...viableOptions.map((option) => (
        option.railLimitMa !== null
          ? `${option.label} rail budget: ${formatCurrent(option.railLimitMa)}`
          : `${option.label} rail is present, but its current budget is not entered yet`
      ))
    ].filter(Boolean);

    return {
      key: 'power-supply',
      label: 'Supply Voltage',
      status: 'pass',
      message: viableOptions.length > 1
        ? `The sensor can run from ${viableLabels.join(' or ')}. ${preferredOption?.label || viableLabels[0]} is the recommended rail.`
        : `The sensor supply range fits the MCU ${viableLabels[0]} rail.`,
      details: detailLines,
      chosenRail: preferredOption
    };
  }

  if (maybeOptions.length > 0) {
    return {
      key: 'power-supply',
      label: 'Supply Voltage',
      status: 'warning',
      message: 'A likely power rail exists, but the sensor supply range is incomplete.',
      details: [
        sensorRangeText ? `Sensor supply range: ${sensorRangeText}` : 'Sensor supply range: not entered',
        ...maybeOptions.map((option) => `${option.label} rail looks available on the MCU`)
      ],
      chosenRail: preferredOption
    };
  }

  if (sensorSupplyMin !== null || sensorSupplyMax !== null) {
    return {
      key: 'power-supply',
      label: 'Supply Voltage',
      status: 'fail',
      message: 'No compatible MCU rail could be confirmed for the sensor supply range.',
      details: [
        sensorRangeText ? `Sensor supply range: ${sensorRangeText}` : 'Sensor supply range: partial data only'
      ],
      chosenRail: null
    };
  }

  return {
    key: 'power-supply',
    label: 'Supply Voltage',
    status: 'unknown',
    message: 'Enter the sensor supply range and MCU rail data to verify power compatibility.',
    details: [],
    chosenRail: null
  };
};

const evaluateCurrentBudget = (sensorBoard, chosenRail) => {
  if (!chosenRail) {
    return {
      key: 'current-budget',
      label: 'Current Budget',
      status: 'unknown',
      message: 'Pick a confirmed power rail before checking current headroom.',
      details: []
    };
  }

  const sensorCurrentMax = toNumber(sensorBoard?.current_draw_max_ma);
  const sensorCurrentTyp = toNumber(sensorBoard?.current_draw_typ_ma);
  const powerPinLimits = (chosenRail.pins || [])
    .map((pin) => toNumber(pin?.current_limit_ma))
    .filter((value) => value !== null);
  const powerPinLimit = powerPinLimits.length > 0 ? Math.min(...powerPinLimits) : null;
  const availableCurrent = [chosenRail.railLimitMa, powerPinLimit]
    .filter((value) => value !== null);
  const effectiveCurrentLimit = availableCurrent.length > 0 ? Math.min(...availableCurrent) : null;

  if (effectiveCurrentLimit === null) {
    return {
      key: 'current-budget',
      label: 'Current Budget',
      status: 'unknown',
      message: `The ${chosenRail.label} rail is known, but its usable current limit is not entered yet.`,
      details: []
    };
  }

  if (sensorCurrentMax !== null) {
    return {
      key: 'current-budget',
      label: 'Current Budget',
      status: sensorCurrentMax <= effectiveCurrentLimit + EPSILON ? 'pass' : 'fail',
      message: sensorCurrentMax <= effectiveCurrentLimit + EPSILON
        ? `The sensor max current draw fits within the MCU ${chosenRail.label} rail budget.`
        : `The sensor max current draw is higher than the MCU ${chosenRail.label} rail budget.`,
      details: [
        `Sensor max current: ${formatCurrent(sensorCurrentMax)}`,
        `MCU available current: ${formatCurrent(effectiveCurrentLimit)}`
      ]
    };
  }

  if (sensorCurrentTyp !== null) {
    return {
      key: 'current-budget',
      label: 'Current Budget',
      status: 'warning',
      message: 'Only the sensor typical current draw is entered, so peak current still needs confirmation.',
      details: [
        `Sensor typical current: ${formatCurrent(sensorCurrentTyp)}`,
        `MCU available current: ${formatCurrent(effectiveCurrentLimit)}`
      ]
    };
  }

  return {
    key: 'current-budget',
    label: 'Current Budget',
    status: 'unknown',
    message: 'Enter the sensor current draw to verify the power budget.',
    details: [
      `MCU available current: ${formatCurrent(effectiveCurrentLimit)}`
    ]
  };
};

const evaluateLogicLevels = (mcuBoard, sensorBoard, receiverPins, recommendedProtocol) => {
  const mcuLogicMax = toNumber(mcuBoard?.logic_voltage_max_v);
  const sensorLogicMin = toNumber(sensorBoard?.logic_voltage_min_v);
  const sensorLogicMax = toNumber(sensorBoard?.logic_voltage_max_v);

  const mcuDrivesSensor = mcuLogicMax !== null
    ? rangeContainsVoltage(sensorLogicMin, sensorLogicMax, mcuLogicMax)
    : null;

  const requiredReceiverCount = recommendedProtocol
    ? (RECEIVER_CAPABILITIES_BY_PROTOCOL[recommendedProtocol] || []).length
    : 0;

  const allReceiverPinsTolerant = requiredReceiverCount > 0
    && receiverPins.length >= requiredReceiverCount
    && receiverPins.every((pin) => Boolean(pin?.is_5v_tolerant));

  if (mcuDrivesSensor === false) {
    return {
      key: 'logic-levels',
      label: 'Logic Levels',
      status: 'fail',
      message: 'The MCU logic level does not fit inside the sensor logic range.',
      details: [
        mcuLogicMax !== null ? `MCU logic level: ${formatDecimal(mcuLogicMax)}V` : null,
        sensorLogicMin !== null || sensorLogicMax !== null
          ? `Sensor logic range: ${formatVoltageRange(sensorLogicMin, sensorLogicMax)}`
          : null
      ].filter(Boolean)
    };
  }

  if (mcuLogicMax !== null && sensorLogicMax !== null) {
    if (sensorLogicMax <= mcuLogicMax + EPSILON) {
      return {
        key: 'logic-levels',
        label: 'Logic Levels',
        status: mcuDrivesSensor === true ? 'pass' : 'warning',
        message: mcuDrivesSensor === true
          ? 'The MCU and sensor logic levels line up for direct signaling.'
          : 'Sensor outputs look safe for the MCU, but the full sensor logic range is incomplete.',
        details: [
          `MCU logic level: ${formatDecimal(mcuLogicMax)}V`,
          `Sensor logic range: ${formatVoltageRange(sensorLogicMin, sensorLogicMax)}`
        ]
      };
    }

    if (allReceiverPinsTolerant) {
      return {
        key: 'logic-levels',
        label: 'Logic Levels',
        status: 'warning',
        message: `Sensor logic can rise above the MCU logic level, but the matched ${getProtocolLabel(recommendedProtocol)} receiver pins are marked 5V tolerant.`,
        details: [
          `MCU logic level: ${formatDecimal(mcuLogicMax)}V`,
          `Sensor logic range: ${formatVoltageRange(sensorLogicMin, sensorLogicMax)}`
        ]
      };
    }

    return {
      key: 'logic-levels',
      label: 'Logic Levels',
      status: 'fail',
      message: 'The sensor logic level can exceed what the MCU inputs safely accept.',
      details: [
        `MCU logic level: ${formatDecimal(mcuLogicMax)}V`,
        `Sensor logic range: ${formatVoltageRange(sensorLogicMin, sensorLogicMax)}`
      ]
    };
  }

  return {
    key: 'logic-levels',
    label: 'Logic Levels',
    status: 'unknown',
    message: 'Enter logic voltage data on both boards to verify signal-level safety.',
    details: []
  };
};

const evaluatePowerPins = (mcuPins, sensorPins, chosenRail) => {
  const mcuGroundPins = findMatchingPins(mcuPins, 'ground', 'mcu');
  const sensorGroundPins = findMatchingPins(sensorPins, 'ground', 'sensor');
  const sensorPowerPins = findMatchingPins(sensorPins, 'power-input', 'sensor');
  const candidatePowerPins = chosenRail?.pins || [
    ...findMatchingPins(mcuPins, 'power-output-3v3', 'mcu'),
    ...findMatchingPins(mcuPins, 'power-output-5v', 'mcu')
  ];

  if (candidatePowerPins.length && mcuGroundPins.length && sensorPowerPins.length && sensorGroundPins.length) {
    return {
      key: 'power-pins',
      label: 'Power Pins',
      status: 'pass',
      message: 'Both boards have power and ground pins tagged for direct wiring.',
      details: [
        `MCU power: ${formatPinList(candidatePowerPins)}`,
        `MCU ground: ${formatPinList(mcuGroundPins)}`,
        `Sensor power input: ${formatPinList(sensorPowerPins)}`,
        `Sensor ground: ${formatPinList(sensorGroundPins)}`
      ]
    };
  }

  return {
    key: 'power-pins',
    label: 'Power Pins',
    status: 'warning',
    message: 'Power or ground pins are not fully tagged yet on one of the boards.',
    details: [
      candidatePowerPins.length ? `MCU power: ${formatPinList(candidatePowerPins)}` : 'MCU power output: not tagged',
      mcuGroundPins.length ? `MCU ground: ${formatPinList(mcuGroundPins)}` : 'MCU ground: not tagged',
      sensorPowerPins.length ? `Sensor power input: ${formatPinList(sensorPowerPins)}` : 'Sensor power input: not tagged',
      sensorGroundPins.length ? `Sensor ground: ${formatPinList(sensorGroundPins)}` : 'Sensor ground: not tagged'
    ]
  };
};

const summarizeOverallStatus = (checks) => {
  const failCount = checks.filter((check) => check.status === 'fail').length;
  const warningCount = checks.filter((check) => check.status === 'warning').length;
  const unknownCount = checks.filter((check) => check.status === 'unknown').length;
  const passCount = checks.filter((check) => check.status === 'pass').length;

  if (failCount > 0) {
    return 'not-compatible';
  }

  if (passCount === 0 || unknownCount >= 3) {
    return 'needs-data';
  }

  if (warningCount > 0 || unknownCount > 0) {
    return 'compatible-with-caveats';
  }

  return 'compatible';
};

const buildOverallSummary = (overallStatus, recommendedProtocol, chosenRail) => {
  const connectionText = recommendedProtocol && chosenRail?.label
    ? `${getProtocolLabel(recommendedProtocol)} with ${chosenRail.label} power`
    : recommendedProtocol
      ? getProtocolLabel(recommendedProtocol)
      : chosenRail?.label
        ? `${chosenRail.label} power`
        : null;

  switch (overallStatus) {
    case 'compatible':
      return connectionText
        ? `This MCU and sensor look directly compatible via ${connectionText}.`
        : 'This MCU and sensor look directly compatible from the available metadata.';
    case 'compatible-with-caveats':
      return connectionText
        ? `A ${connectionText} hookup looks possible, but review the caveats below first.`
        : 'This pairing looks possible, but review the caveats below first.';
    case 'not-compatible':
      return 'This pairing has at least one direct compatibility conflict.';
    case 'needs-data':
    default:
      return 'The selected boards may still work together, but the uploaded metadata is not complete enough to confirm it yet.';
  }
};

const buildRecommendedConnection = (recommendedProtocol, chosenRail) => {
  if (recommendedProtocol && chosenRail?.label) {
    return `${getProtocolLabel(recommendedProtocol)} on ${chosenRail.label}`;
  }

  if (recommendedProtocol) {
    return getProtocolLabel(recommendedProtocol);
  }

  if (chosenRail?.label) {
    return `${chosenRail.label} power`;
  }

  return '';
};

export const isSmartCompareMicrocontroller = (board) => MCU_CATEGORIES.has(String(board?.category || '').trim());
export const isSmartCompareSensor = (board) => SENSOR_CATEGORIES.has(String(board?.category || '').trim());

export const buildSmartCompare = (boardA, pinsA, boardB, pinsB) => {
  const boardAIsMcu = isSmartCompareMicrocontroller(boardA);
  const boardAIsSensor = isSmartCompareSensor(boardA);
  const boardBIsMcu = isSmartCompareMicrocontroller(boardB);
  const boardBIsSensor = isSmartCompareSensor(boardB);

  if (!((boardAIsMcu && boardBIsSensor) || (boardBIsMcu && boardAIsSensor))) {
    return {
      available: false,
      reason: 'Smart compare currently runs when one microcontroller or development board and one sensor board are selected.'
    };
  }

  const mcuBoard = boardAIsMcu ? boardA : boardB;
  const sensorBoard = boardAIsSensor ? boardA : boardB;
  const mcuPins = boardAIsMcu ? pinsA : pinsB;
  const sensorPins = boardAIsSensor ? pinsA : pinsB;

  const protocolOverlap = evaluateProtocolOverlap(mcuBoard, sensorBoard);
  const protocolPins = evaluateProtocolPins(protocolOverlap.sharedProtocols, mcuPins, sensorPins);
  const powerSupply = evaluatePowerSupply(mcuBoard, sensorBoard, mcuPins);
  const currentBudget = evaluateCurrentBudget(sensorBoard, powerSupply.chosenRail);
  const logicLevels = evaluateLogicLevels(
    mcuBoard,
    sensorBoard,
    protocolPins.receiverPins || [],
    protocolPins.recommendedProtocol
  );
  const powerPins = evaluatePowerPins(mcuPins, sensorPins, powerSupply.chosenRail);

  const checks = [
    protocolOverlap,
    protocolPins,
    powerSupply,
    currentBudget,
    logicLevels,
    powerPins
  ];

  const overallStatus = summarizeOverallStatus(checks);
  const recommendedConnection = buildRecommendedConnection(protocolPins.recommendedProtocol, powerSupply.chosenRail);
  const notes = [
    sensorBoard?.protocol_notes ? `Sensor notes: ${sensorBoard.protocol_notes}` : null,
    mcuBoard?.protocol_notes ? `MCU notes: ${mcuBoard.protocol_notes}` : null
  ].filter(Boolean);

  return {
    available: true,
    overallStatus,
    summary: buildOverallSummary(overallStatus, protocolPins.recommendedProtocol, powerSupply.chosenRail),
    recommendedConnection,
    boards: {
      mcu: mcuBoard,
      sensor: sensorBoard
    },
    checks,
    notes
  };
};
